// Writing an EFP plan: deal tickets, allocations, the basis draw-down, and the contract
// filed against what it created.
//
// Two tickets are written, not one. The parent leg closes a hedge held against the
// futures broker, while the four plant legs belong to the supplier's contract - they
// carry different counterparties, so they cannot share a ticket.
import {
    AllocationModel,
    PositionModel,
    UpdateAllocationRequest,
    UpdateDealTicketRequest,
    UpdatePositionModel,
} from '../../../../Generated/Commodity-Management-Api';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { EfpLeg, EfpPlan } from './EfpPositionPlan';

export interface EfpSubmitInput {
    commodityId: number;
    /** Seller on the contract - the counterparty for the plant legs. */
    counterpartyId: number;
    /**
     * Counterparty on the parent's existing futures, used for the hedge-closing ticket.
     * Falls back to the contract's seller when the existing futures carry none.
     */
    parentCounterpartyId?: number;
    allocationGroup?: AllocationModel;
    tradeDate: string;
    dealTicketNumber?: string;
    /** The confirmation itself, filed against the contract ticket when supplied. */
    contractFile?: File;
}

export interface EfpSubmitResult {
    /** Deal ticket holding the four plant legs. */
    contractDealTicketId?: number;
    /** Deal ticket holding the parent hedge close. */
    parentDealTicketId?: number;
    allocationCount: number;
    basisReduced: boolean;
    documentStored: boolean;
    errors: string[];
}

function toPositionModel(leg: EfpLeg): UpdatePositionModel {
    return {
        positionId: undefined,
        locationId: leg.locationId,
        positionType: leg.positionType,
        side: leg.side,
        volume: leg.volume,
        price: leg.price,
        unit: leg.unit,
        currency: leg.currency,
        budgetDate: leg.budgetDate,
        contractDate: leg.contractDate,
    } as UpdatePositionModel;
}

/** An existing position sent back unchanged, so re-sending a ticket does not lose it. */
function toUnchangedPositionModel(position: PositionModel): UpdatePositionModel {
    return {
        positionId: position.positionId,
        locationId: position.location?.locationId,
        positionType: position.positionType,
        side: position.side,
        volume: position.volume,
        price: position.price,
        unit: position.unit,
        currency: position.currency,
        budgetDate: position.budgetDate,
        contractDate: position.contractDate,
        optionStrike: position.optionStrike,
        optionType: position.optionType,
        optionStyle: position.optionStyle,
        customData: position.customData,
    } as UpdatePositionModel;
}

/**
 * Reduce the matched basis position by the contract volume.
 *
 * The whole ticket is read back and re-sent because UpdateDealTicketService hard-deletes
 * every position on the ticket that the request omits - sending the basis position alone
 * would silently delete its siblings.
 */
async function reduceBasisPosition(plan: EfpPlan, tradeDate: string): Promise<void> {
    const reduction = plan.basisReduction!;

    const { data } = await CommodityManagementApi.getDealTicket(null, String(reduction.dealTicketId));
    const dealTicket = data.dealTicket!;

    const positions = (dealTicket.positions ?? []).map(position =>
        position.positionId === reduction.positionId
            ? { ...toUnchangedPositionModel(position), volume: reduction.newVolume }
            : toUnchangedPositionModel(position));

    const request: UpdateDealTicketRequest = {
        dealTicketId: dealTicket.dealTicketId,
        tradeDate: dealTicket.tradeDate ?? tradeDate,
        commodityId: dealTicket.commodity!.commodityId,
        counterpartyId: dealTicket.counterparty?.counterpartyId,
        portfolioId: dealTicket.portfolio?.portfolioId,
        dealTicketNumber: dealTicket.dealTicketNumber,
        positions,
    } as UpdateDealTicketRequest;

    await CommodityManagementApi.addTrade(null, request);
}

export async function submitEfpPlan(plan: EfpPlan, input: EfpSubmitInput): Promise<EfpSubmitResult> {
    const result: EfpSubmitResult = {
        allocationCount: 0,
        basisReduced: false,
        documentStored: false,
        errors: [],
    };

    if (plan.errors.length > 0) {
        return { ...result, errors: ['The plan has unresolved errors and was not submitted', ...plan.errors] };
    }

    const parentLegs = plan.legs.filter(leg => !leg.allocate);
    const plantLegs = plan.legs.filter(leg => leg.allocate);

    // The hedge close first: if it fails, nothing else has been written.
    if (parentLegs.length > 0) {
        const parentTicket: UpdateDealTicketRequest = {
            tradeDate: input.tradeDate,
            commodityId: input.commodityId,
            counterpartyId: input.parentCounterpartyId ?? input.counterpartyId,
            positions: parentLegs.map(toPositionModel),
        } as UpdateDealTicketRequest;

        const response = await CommodityManagementApi.addTrade(null, parentTicket);
        result.parentDealTicketId = response.data.dealTicketId ?? undefined;
    }

    const contractTicket: UpdateDealTicketRequest = {
        tradeDate: input.tradeDate,
        commodityId: input.commodityId,
        counterpartyId: input.counterpartyId,
        dealTicketNumber: input.dealTicketNumber,
        positions: plantLegs.map(toPositionModel),
    } as UpdateDealTicketRequest;

    const contractResponse = await CommodityManagementApi.addTrade(null, contractTicket);
    result.contractDealTicketId = contractResponse.data.dealTicketId ?? undefined;

    // Allocate the plant legs. The ticket is read back because allocations key off the
    // position ids the server assigned.
    if (input.allocationGroup && result.contractDealTicketId) {
        const { data } = await CommodityManagementApi.getDealTicket(null, String(result.contractDealTicketId));
        const created = data.dealTicket?.positions ?? [];

        for (const position of created) {
            const request: UpdateAllocationRequest = {
                allocationId: 0,
                allocationGroupId: Number(input.allocationGroup.allocationGroupId),
                positionId: position.positionId!,
                volume: position.volume ?? 0,
            };

            try {
                await CommodityManagementApi.addAllocation(null, request);
                result.allocationCount++;
            } catch (error) {
                result.errors.push(
                    `Position ${position.positionId} was created but not allocated: `
                    + `${(error as Error)?.message ?? 'allocation failed'}`
                );
            }
        }
    }

    if (plan.basisReduction) {
        try {
            await reduceBasisPosition(plan, input.tradeDate);
            result.basisReduced = true;
        } catch (error) {
            result.errors.push(
                `Positions were created but the basis position was not reduced: `
                + `${(error as Error)?.message ?? 'update failed'}`
            );
        }
    }

    // Filed against the contract ticket, so the dashboard can show the confirmation
    // wherever one of its positions or allocations appears.
    if (input.contractFile && result.contractDealTicketId) {
        try {
            await CommodityManagementApi.upload(
                null,
                EFP_DOCUMENT_ENTITY,
                'new',
                input.contractFile,
                result.contractDealTicketId,
                input.dealTicketNumber ? `EFP contract ${input.dealTicketNumber}` : 'EFP contract',
            );
            result.documentStored = true;
        } catch (error) {
            result.errors.push(
                `Positions were created but the contract was not stored: `
                + `${(error as Error)?.message ?? 'upload failed'}`
            );
        }
    }

    return result;
}

/** Entity the confirmation is filed under, for upload and for listing it back. */
export const EFP_DOCUMENT_ENTITY = 'DealTickets';

/** Contracts filed against a deal ticket, for the dashboard. */
export function listEfpContractsForDealTicket(dealTicketId: number) {
    return CommodityManagementApi.listDocuments(null, EFP_DOCUMENT_ENTITY, dealTicketId);
}
